import React, { useEffect, useState } from 'react';
import { Users, Calendar, Trophy, School, TrendingUp, DollarSign } from 'lucide-react';
import Navigation from '../components/Navigation';
import { bookingAPI, membershipAPI, partnershipAPI, adminAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, membershipsRes, partnershipsRes] = await Promise.all([
        adminAPI.getStats(),
        bookingAPI.getAll(),
        membershipAPI.getAll(),
        partnershipAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
      setMemberships(membershipsRes.data.memberships);
      setPartnerships(partnershipsRes.data.partnerships);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBooking = async (bookingId, status) => {
    try {
      await bookingAPI.update(bookingId, { status });
      fetchData();
    } catch (error) {
      console.error('Error updating booking:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-xl text-slate-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Admin Dashboard</h1>
            <p className="text-lg text-slate-600">Manage bookings, memberships, and partnerships</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card data-testid="stat-bookings">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Total Bookings</CardTitle>
                <Calendar className="w-5 h-5 text-cyan-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_bookings || 0}</div>
                <p className="text-xs text-slate-500 mt-1">
                  {stats?.confirmed_bookings || 0} confirmed
                </p>
              </CardContent>
            </Card>

            <Card data-testid="stat-members">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Team Members</CardTitle>
                <Trophy className="w-5 h-5 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_members || 0}</div>
                <p className="text-xs text-slate-500 mt-1">Marlins swimmers</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-partnerships">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Partnerships</CardTitle>
                <School className="w-5 h-5 text-purple-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">{stats?.total_partnerships || 0}</div>
                <p className="text-xs text-slate-500 mt-1">School inquiries</p>
              </CardContent>
            </Card>

            <Card data-testid="stat-revenue">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">Active Status</CardTitle>
                <TrendingUp className="w-5 h-5 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">Live</div>
                <p className="text-xs text-slate-500 mt-1">System operational</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Bookings */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Recent Lesson Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bookings-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-slate-600 font-semibold">Student</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Type</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Date & Time</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Status</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Payment</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.slice(0, 10).map((booking) => (
                      <tr key={booking.id} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{booking.student_name}</div>
                          <div className="text-sm text-slate-500">{booking.student_email}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{booking.lesson_type}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="text-slate-900">{booking.lesson_date}</div>
                          <div className="text-sm text-slate-500">{booking.lesson_time}</div>
                        </td>
                        <td className="p-3">
                          <Badge
                            variant={booking.status === 'confirmed' ? 'default' : 'secondary'}
                          >
                            {booking.status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge
                            variant={booking.payment_status === 'success' ? 'default' : 'destructive'}
                          >
                            {booking.payment_status}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {booking.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateBooking(booking.id, 'confirmed')}
                              data-testid={`confirm-booking-${booking.id}`}
                            >
                              Confirm
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Team Memberships */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-2xl">Marlins Team Members</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="memberships-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-slate-600 font-semibold">Swimmer</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Age</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Parent</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Type</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Status</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberships.slice(0, 10).map((membership) => (
                      <tr key={membership.id} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{membership.swimmer_name}</div>
                          <div className="text-sm text-slate-500">{membership.swimmer_email}</div>
                        </td>
                        <td className="p-3">{membership.swimmer_age}</td>
                        <td className="p-3">
                          <div className="text-slate-900">{membership.parent_name}</div>
                          <div className="text-sm text-slate-500">{membership.parent_email}</div>
                        </td>
                        <td className="p-3">
                          <Badge variant="outline">{membership.membership_type}</Badge>
                        </td>
                        <td className="p-3">
                          <Badge variant="default">{membership.status}</Badge>
                        </td>
                        <td className="p-3">
                          <Badge
                            variant={membership.payment_status === 'success' ? 'default' : 'destructive'}
                          >
                            {membership.payment_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* School Partnerships */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">School Partnership Inquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="partnerships-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3 text-slate-600 font-semibold">School</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Contact Person</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Email</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Phone</th>
                      <th className="text-left p-3 text-slate-600 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnerships.map((partnership) => (
                      <tr key={partnership.id} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium text-slate-900">{partnership.school_name}</td>
                        <td className="p-3">{partnership.contact_person}</td>
                        <td className="p-3">{partnership.contact_email}</td>
                        <td className="p-3">{partnership.contact_phone}</td>
                        <td className="p-3">
                          <Badge variant="secondary">{partnership.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
